/**
 * The pending-changes sub-store every inspector exposes. The concrete inspector
 * stores (state/method/fsm/...) each declare their own `*ChangeKey` union and
 * `storeChange` signature; this hook is generic over all of them, so it is typed
 * against the minimal structural surface it touches. `V` is the property's value
 * type (a string or `null` in practice).
 */
interface InspectorChanges<V> {
  isDirty(key: string): boolean;
  storeChange(key: string, original: V, value: V): void;
}

interface InspectorWithChanges<V> {
  changes: InspectorChanges<V>;
}

/**
 * Custom hook to make working with changes in the context of an inspector ui
 * store easier.
 *
 * @param ui the ui store
 * @param element the original element
 * @param key the key of the property
 *
 * @return An array of:
 * - value: the current value of the property, taking into account the changes
 *   in the ui store
 * - onChange: an onChange handler that takes a single argument: the new value
 * - dirty: is the value dirty?
 */
export function useInspectorChange<V>(
  ui: InspectorWithChanges<V>,
  element: Record<string, V>,
  key: string
): [V, (value: V) => void, boolean] {
  const dirty = ui.changes.isDirty(key);
  // `changes[key]` is read dynamically by property name; index past the typed
  // method surface once to recover the stored value of type `V`.
  const changes = ui.changes as InspectorChanges<V> & Record<string, V>;
  const value = dirty ? changes[key] : element[key];
  const onChange = (value: V) => ui.changes.storeChange(key, element[key], value);
  return [value, onChange, dirty];
}
